import { useState } from "react";
import { Redirect } from "react-router-dom";
import { Table } from "react-bootstrap";
import { useModuleEnabled } from "redux/selectors/modules";
import Card from "components/core/basic/Card";
import checkTierAccess from "utils/checkTierAccess";
import { trellisPalette } from "components/custom/analytics/palettes";
import { Button } from "components/core/basic/Button";
import ImportButton from "components/custom/import/ImportButton";
import Accordion from "components/core/basic/Accordion";
import { DevErrorDisplay } from "components/core/blocks/DevErrorDisplay";
import { toTitleCase } from "utils/formatText";
import { useAppSelector } from "../../redux/hooks";
import { usePricingConfiguration } from "./api/usePricingConfiguration";
import Loading from "../../components/core/blocks/Loading";
import { PageContainer } from "../../components/core/basic/PageContainer";
import { css } from "../../../styled-system/css";
import { Center, Container, Grid, HStack, VStack } from "../../../styled-system/jsx";
import { Select } from "../../components/fields/formily/Select";
import { ExplainerBox } from "../../components/core/basic/ExplainerBox";
import { NumberInput, OffsetInput } from "../../components/fields/formily/NumberInput";
import { NumberInput as NumberInputBase } from "../../components/fields/base/NumberInput";
import { type Offset } from "zodios/apiDefinitions/pricing";
import { useFormily } from "../../hooks/useFormily";
import { ArrayField, Field, FormConsumer, FormProvider, observer, useForm } from "@formily/react";
import { TextField } from "../../components/fields/base/TextField";
import { applyOffset } from "./utils";
import { isField } from "@formily/core";
import { Checkbox } from "../../components/fields/formily/Checkbox";
import { successToast, errorToast } from "../../utils/toasts";
import type { PricingOrg } from "../../zodios/apiDefinitions/pricing";
import { useUpdatePricingConfiguration } from "./api/useUpdatePricingConfiguration";
import { gridItem } from "../../../styled-system/patterns";

const defaultInventoryRules = [
  {
    status: "deficient",
    min: 0,
    max: 50,
  },
  {
    status: "healthy",
    min: 50,
    max: 150,
  },
  {
    status: "excess",
    min: 150,
    max: 300,
  },
  {
    status: "overstock",
    min: 300,
    max: 99999999,
  },
];

const ruleToStrategyMap = {
  deficient: "Increase Prices",
  healthy: "Balanced Strategy",
  excess: "Decrease Prices",
  overstock: "Aggressive Price Reduction",
  default: "Unknown",
};

function getInventoryRuleStatus(
  current: number,
  desired: number,
  rules: { min: number; max: number; status: string }[],
) {
  for (const rule of rules) {
    const minLevel = (rule.min / 100) * desired;
    const maxLevel = (rule.max / 100) * desired;

    if (current >= minLevel && current <= maxLevel) {
      return rule.status as "deficient" | "healthy" | "excess" | "overstock";
    }
  }

  return null;
}

const Settings = () => {
  const user = useAppSelector((state) => state.user);
  const amz_onboard = useAppSelector((state) => state.amz_onboard);
  const hasPriceOptimizationModule = useModuleEnabled("PRICE_OPTIMIZATION");
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [importRelationshipsModalOpen, setImportRelationshipsModalOpen] = useState(false);
  const configurationQuery = usePricingConfiguration();
  const updateMutation = useUpdatePricingConfiguration();

  if (
    ((!user.pricing_tier || user.pricing_tier === "unknown") &&
      !hasPriceOptimizationModule &&
      !checkTierAccess(user.tier, "pricing", amz_onboard.org_status)) ||
    user.pricing_tier === "MANUALLY_DISABLED"
  ) {
    return <Redirect to="/user/pricing" />;
  }

  if (configurationQuery.isLoading) {
    return <Loading fullPage text={"Loading Configuration"} />;
  }

  if (!configurationQuery.isSuccess) {
    return <DevErrorDisplay userMessage="Failed to load configuration" error={configurationQuery.error} />;
  }

  return (
    <PageContainer>
      <Container>
        <h1
          className={css({
            margin: "0",
            padding: "small",
            color: "text.heading",
            fontSize: "xxlarge",
            fontWeight: "bold",
            textAlign: "center",
          })}
        >
          Dynamic Pricing - Configuration
        </h1>

        <HStack justify={"end"} padding="medium">
          <ImportButton
            defaultType={`pricing_bulk_upload`}
            importModalOpen={importModalOpen}
            setImportModalOpen={setImportModalOpen}
            title={"Upload Pricing Data"}
          />

          <ImportButton
            defaultType={`pricing_relationships`}
            importModalOpen={importRelationshipsModalOpen}
            setImportModalOpen={setImportRelationshipsModalOpen}
            title={"Upload Relationships"}
          />
        </HStack>
        <PricingConfigurationForm
          data={configurationQuery.data}
          onSubmit={async (values) => {
            await updateMutation.mutateAsync(
              {
                ...values,
                porgId: configurationQuery.data.id,
              },
              {
                onSuccess: () => {
                  successToast({
                    message: `Pricing settings have been saved`,
                  });
                },
                onError: (error) => {
                  errorToast({ message: `Error saving pricing settings`, error });
                },
              },
            );
          }}
        />
      </Container>
    </PageContainer>
  );
};

function ConfPageLabel({ label, description }: { label: string; description: string }) {
  return (
    <VStack gap="xsmall" alignItems={"start"}>
      <h5 className={css({ margin: 0, color: "text.heading", fontSize: "xlarge", fontWeight: 700 })}>{label}</h5>
      <div className={css({ color: "text.heading", fontSize: "small" })}>{description}</div>
    </VStack>
  );
}

type FormValues = PricingOrg & {
  has_inventory_rules: boolean;
  inventory_rules: Exclude<PricingOrg["supply_target_program"], null>["rules"] | null;
  b2b_offset: Offset;
  list_price_offset: Offset;
};

function porgToFormValues(porg: PricingOrg): FormValues {
  const result = {
    ...porg,
    has_inventory_rules: !!porg.supply_target_program,
    inventory_rules: porg.supply_target_program ? porg.supply_target_program.rules : null,
    b2b_offset: {
      amount: porg.b2b_offset_amount,
      type: porg.b2b_offset_type,
    },
    list_price_offset: {
      amount: porg.list_price_offset_amount,
      type: porg.list_price_offset_type,
    },
  };

  return result;
}

const PricesErrorMessage = observer(() => {
  const field = useForm().getFieldState("send_standard_price");
  return field.selfErrors ?
      <p className={gridItem({ colSpan: 2, margin: 0, color: "error", fontSize: "small" })}>{field.selfErrors[0]}</p>
    : null;
});

function PricingConfigurationForm({
  data,
  onSubmit,
}: {
  data: Exclude<ReturnType<typeof usePricingConfiguration>["data"], undefined>;
  onSubmit: (values: FormValues) => void | Promise<void>;
}) {
  const form = useFormily<FormValues>({
    initialValues: porgToFormValues(data),
  });
  const [testInventoryLevel, setTestInventoryLevel] = useState(40);
  const [testDesiredInventoryLevel, setTestDesiredInventoryLevel] = useState(30);
  return (
    <FormProvider form={form}>
      <FormConsumer>
        {() => (
          <Card
            className={css({
              padding: "large",
            })}
          >
            <Grid
              columnGap={"xlarge"}
              rowGap="medium"
              alignItems={"start"}
              gridTemplateColumns={"minmax(0,2fr) minmax(0,1fr)"}
              gridAutoRows={"1fr"}
            >
              <ConfPageLabel
                label="Charm Pricing Style"
                description="The digits you want to end your price with. i.e. 7.99, 129.95"
              />
              <Field
                name={"end_digits"}
                component={[Select]}
                dataSource={[
                  { value: "0.99", label: "0.99" },
                  { value: "0.98", label: "0.98" },
                  { value: "0.97", label: "0.97" },
                  { value: "0.96", label: "0.96" },
                  { value: "0.95", label: "0.95" },
                  { value: "nocharm", label: "No charm" },
                  { value: "rounded", label: "Rounded" },
                ]}
              />
              <ConfPageLabel
                label="Weekly Pricing Cycle"
                description="If enabled, Dynamic Pricing will evaluate price changes on a weekly cycle."
              />
              <Field
                name={"weekly_cycle"}
                component={[Select]}
                dataSource={[
                  { value: false, label: "No" },
                  { value: true, label: "Yes" },
                ]}
              />

              <ConfPageLabel
                label="Use Imported Inventory for Days on Hand"
                description="Use the imported inventory to calculate Days on Hand threshold for pricing rules"
              />
              <Field
                name={"use_incoming_inventory"}
                component={[Select]}
                dataSource={[
                  { value: false, label: "No" },
                  { value: true, label: "Yes" },
                ]}
              />
              <ConfPageLabel
                label="Enable Advanced Days on Hand Rules"
                description="Enable the more advanced Days on Hand rules to help you manage your inventory levels"
              />
              <Field
                name={"has_inventory_rules"}
                component={[Select]}
                dataSource={[
                  { value: false, label: "No" },
                  { value: true, label: "Yes" },
                ]}
                reactions={(field) => {
                  if (field.value) {
                    form.setValuesIn("inventory_rules", data.supply_target_program?.rules ?? defaultInventoryRules);
                  } else {
                    form.setValuesIn("inventory_rules", null);
                  }
                }}
              />
            </Grid>
            <Accordion title={"Prices to send"} initialExpanded>
              <Grid rowGap="medium" justifyItems={"start"} columns={2}>
                <Field
                  name={"send_standard_price"}
                  title={"Your Price"}
                  component={[Checkbox]}
                  reactions={(field) => {
                    field.query(".send_discounted_price").take((f) => {
                      if (!isField(f)) return;

                      if (!field.value && !f.value) {
                        field.selfErrors = ['At least one of "Your Price" or "Sales Price" must be sent'];
                      } else {
                        field.selfErrors = [];
                      }
                    });
                  }}
                />
                <Field
                  name={"send_discounted_price"}
                  title={"Sales Price"}
                  component={[Checkbox]}
                  reactions={(field) => {
                    field.query(".send_standard_price").take((f) => {
                      if (!isField(f)) return;

                      if (!field.value && !f.value) {
                        f.selfErrors = ['At least one of "Your Price" or "Sales Price" must be sent'];
                      } else {
                        f.selfErrors = [];
                      }
                    });
                  }}
                />
                <PricesErrorMessage />
                <Field
                  name={"send_b2b_price"}
                  title={"B2B Price"}
                  component={[Checkbox]}
                  reactions={(field) => {
                    field.query(".b2b_offset").take((f) => {
                      if (!isField(f)) return;
                      f.disabled = !field.value;
                      if (!field.value) {
                        f.value = { amount: 0, type: "percent" };
                      }
                    });
                  }}
                />
                <Field
                  name={"send_list_price"}
                  title={"List Price"}
                  component={[Checkbox]}
                  reactions={(field) => {
                    field.query(".list_price_offset").take((f) => {
                      if (!isField(f)) return;
                      f.disabled = !field.value;
                      if (!field.value) {
                        f.value = { amount: 0, type: "percent" };
                      }
                    });
                  }}
                />
                <Field
                  name={"b2b_offset"}
                  title={"B2B Offset"}
                  component={[OffsetInput]}
                  validator={(value) => (value.amount > 0 ? "B2B offset must be negative to give a discount" : true)}
                  reactions={(field) => {
                    field.query(".send_b2b_price").take((f) => {
                      if (!isField(f)) return;
                      if (f.value) {
                        field.description = `If B2C price is 110, B2B price is going to be ${applyOffset(110, field.value.amount, field.value.type)} `;
                      } else {
                        field.description = `B2B price won't be sent`;
                      }
                    });
                  }}
                />
                <Field
                  name={"list_price_offset"}
                  title={"List Price Offset"}
                  component={[OffsetInput]}
                  validator={(value) =>
                    value.amount < 0 ? "List price should exceed or be equal to offer price" : true
                  }
                  reactions={(field) => {
                    field.query(".send_list_price").take((f) => {
                      if (!isField(f)) return;
                      if (f.value) {
                        field.description = `If sell price is 110, list price is going to be ${applyOffset(110, field.value.amount, field.value.type)} `;
                      } else {
                        field.description = `List price won't be sent`;
                      }
                    });
                  }}
                />
              </Grid>
            </Accordion>
            <Accordion title={"Advanced Settings"} initialExpanded={false}>
              <Grid
                columnGap={"xlarge"}
                rowGap="medium"
                alignItems={"start"}
                gridTemplateColumns={"minmax(0,2fr) minmax(0,1fr)"}
                gridAutoRows={"1fr"}
              >
                <ConfPageLabel
                  label="Look Back Days"
                  description="How many historical days does the pricing algorithm use to make decisions."
                />
                <Field
                  name={"days_back"}
                  component={[
                    NumberInput,
                    {
                      minValue: 0,
                      formatOptions: {
                        maximumFractionDigits: 0,
                      },
                    },
                  ]}
                />

                <ConfPageLabel
                  label="Sales Threshold"
                  description="How many units sold does the price to have in order to be considered valid by Dynamic Pricing."
                />
                <Field
                  name={"sales_threshold"}
                  component={[
                    NumberInput,
                    {
                      minValue: 0,
                      formatOptions: {
                        maximumFractionDigits: 0,
                      },
                    },
                  ]}
                />

                <ConfPageLabel
                  label="Days with Price"
                  description="How many days does a price need to have been offered in order to be considered valid by Dynamic Pricing."
                />
                <Field
                  name={"days_with_price"}
                  component={[
                    NumberInput,
                    {
                      minValue: 0,
                      formatOptions: {
                        maximumFractionDigits: 0,
                      },
                    },
                  ]}
                />
              </Grid>
            </Accordion>

            {form.values.has_inventory_rules ?
              <Accordion title={"Days on Hand Rules"} initialExpanded={false}>
                <VStack>
                  <ExplainerBox>
                    The rule set below enables you to adjust your pricing strategies based on the remaining days of
                    inventory on hand. The minimum and maximum limits compare the current Days on Hand to the Days on
                    Hand target specified in each Dynamic Pricing plan’s configuration. Their purpose is to ensure stock
                    retention when inventory is low, enhance margins when inventory levels are stable, and reduce prices
                    to increase sell-through rates during excess or overstock situations.
                  </ExplainerBox>
                  <Table bordered>
                    <thead>
                      <tr>
                        <th style={{ width: "250px" }}>Status</th>
                        <th style={{ width: "180px" }}>Minimum (%)</th>
                        <th style={{ width: "180px" }}>Maximum (%)</th>
                        <th>Example</th>
                      </tr>
                    </thead>
                    <tbody>
                      <ArrayField name={"inventory_rules"}>
                        {(inventoryRulesField) => (
                          <>
                            <tr>
                              <td>
                                <strong>Deficient</strong>
                                <div>
                                  <small>Running out of stock soon</small>
                                </div>
                              </td>
                              <td>
                                <Field
                                  name={"0.min"}
                                  component={[
                                    NumberInput,
                                    {
                                      endIcon: "%",
                                      className: css({
                                        maxWidth: "150px",
                                      }),
                                    },
                                  ]}
                                  disabled
                                />
                              </td>
                              <td>
                                <Field
                                  name={"0.max"}
                                  component={[
                                    NumberInput,
                                    {
                                      endIcon: "%",
                                      className: css({
                                        maxWidth: "150px",
                                      }),
                                    },
                                  ]}
                                  reactions={(field) => {
                                    field.query("..[+].min").take((f) => {
                                      if (!isField(f)) return;
                                      f.value = field.value;
                                    });
                                  }}
                                />
                              </td>
                              <td>
                                {testDesiredInventoryLevel && testInventoryLevel ?
                                  <span>
                                    {"If the pricing plan has between "}
                                    <span className="fw-bold" style={{ color: trellisPalette[14] }}>
                                      {inventoryRulesField.value[0].min}
                                    </span>
                                    {" to "}
                                    <span className="fw-bold" style={{ color: trellisPalette[14] }}>
                                      {(inventoryRulesField.value[0].max / 100) * testDesiredInventoryLevel}
                                    </span>
                                    {
                                      " days of inventory on hand, it is considered deficient. Pricing plans within this range will adopt a more aggressive strategy to increase prices."
                                    }
                                  </span>
                                : <span>
                                    {"If the pricing plan is within "}
                                    <span className="fw-bold" style={{ color: trellisPalette[14] }}>
                                      {inventoryRulesField.value[0].min}%
                                    </span>{" "}
                                    to{" "}
                                    <span className="fw-bold" style={{ color: trellisPalette[14] }}>
                                      {inventoryRulesField.value[0].max}%
                                    </span>{" "}
                                    of my desired inventory level, I am considered deficient. Pricing plans in this
                                    range will be more aggressive in increasing prices.
                                  </span>
                                }
                              </td>
                            </tr>
                            <tr>
                              <td>
                                <strong>Healthy</strong>
                                <div>
                                  <small>Balanced inventory levels</small>
                                </div>
                              </td>
                              <td>
                                <Field
                                  name={"1.min"}
                                  component={[
                                    NumberInput,
                                    {
                                      endIcon: "%",
                                      className: css({
                                        maxWidth: "150px",
                                      }),
                                    },
                                  ]}
                                  disabled
                                />
                              </td>
                              <td>
                                <Field
                                  name={"1.max"}
                                  component={[
                                    NumberInput,
                                    {
                                      endIcon: "%",
                                      className: css({
                                        maxWidth: "150px",
                                      }),
                                    },
                                  ]}
                                  reactions={(field) => {
                                    field.query("..[+].min").take((f) => {
                                      if (!isField(f)) return;
                                      f.value = field.value;
                                    });
                                  }}
                                />
                              </td>
                              <td>
                                {testDesiredInventoryLevel && testInventoryLevel ?
                                  <span>
                                    If the pricing plan has between{" "}
                                    <span className="fw-bold" style={{ color: trellisPalette[14] }}>
                                      {(inventoryRulesField.value[1].min / 100) * testDesiredInventoryLevel}
                                    </span>{" "}
                                    to{" "}
                                    <span className="fw-bold" style={{ color: trellisPalette[14] }}>
                                      {(inventoryRulesField.value[1].max / 100) * testDesiredInventoryLevel}
                                    </span>{" "}
                                    days of inventory on hand, it is considered healthy. Pricing plans in this range
                                    will be follow your chosen strategy (e.g. volume, balanced or margin)
                                  </span>
                                : <span>
                                    If the pricing plan is within{" "}
                                    <span className="fw-bold" style={{ color: trellisPalette[14] }}>
                                      {inventoryRulesField.value[1].min}%
                                    </span>{" "}
                                    to{" "}
                                    <span className="fw-bold" style={{ color: trellisPalette[14] }}>
                                      {inventoryRulesField.value[1].max}%{" "}
                                    </span>
                                    of my desired inventory level, it am considered healthy. Pricing plans in this range
                                    will be follow your chosen strategy (e.g. volume, balanced or margin)
                                  </span>
                                }
                              </td>
                            </tr>
                            <tr>
                              <td>
                                <strong>Excess</strong>
                                <div>
                                  <small>Sell through rate too low</small>
                                </div>
                              </td>
                              <td>
                                <Field
                                  name={"2.min"}
                                  component={[
                                    NumberInput,
                                    {
                                      endIcon: "%",
                                      className: css({
                                        maxWidth: "150px",
                                      }),
                                    },
                                  ]}
                                  disabled
                                />
                              </td>
                              <td>
                                <Field
                                  name={"2.max"}
                                  component={[
                                    NumberInput,
                                    {
                                      endIcon: "%",
                                      className: css({
                                        maxWidth: "150px",
                                      }),
                                    },
                                  ]}
                                  reactions={(field) => {
                                    field.query("..[+].min").take((f) => {
                                      if (!isField(f)) return;
                                      f.value = field.value;
                                    });
                                  }}
                                />
                              </td>
                              <td>
                                {testDesiredInventoryLevel && testInventoryLevel ?
                                  <span>
                                    If the pricing plan has between{" "}
                                    <span className="fw-bold" style={{ color: trellisPalette[14] }}>
                                      {(inventoryRulesField.value[2].min / 100) * testDesiredInventoryLevel}
                                    </span>{" "}
                                    to{" "}
                                    <span className="fw-bold" style={{ color: trellisPalette[14] }}>
                                      {(inventoryRulesField.value[2].max / 100) * testDesiredInventoryLevel}
                                    </span>{" "}
                                    on hand, it is considered to have excess inventory. Pricing plans in this range will
                                    lean towards price cuts to increase the sell through rate.
                                  </span>
                                : <span>
                                    If the pricing plan is within{" "}
                                    <span className="fw-bold" style={{ color: trellisPalette[14] }}>
                                      {inventoryRulesField.value[2].min}
                                    </span>
                                    % to{" "}
                                    <span className="fw-bold" style={{ color: trellisPalette[14] }}>
                                      {inventoryRulesField.value[2].max}
                                    </span>
                                    % of my desired days on hand, there is excess inventory. Pricing plans in this range
                                    will lean towards price cuts to increase the sell through rate.
                                  </span>
                                }
                              </td>
                            </tr>
                            <tr>
                              <td>
                                <strong>Overstock</strong>
                                <div>
                                  <small>Clearance needed to increase sales</small>
                                </div>
                              </td>
                              <td>
                                <Field
                                  name={"3.min"}
                                  component={[
                                    NumberInput,
                                    {
                                      endIcon: "%",
                                      className: css({
                                        maxWidth: "150px",
                                      }),
                                    },
                                  ]}
                                  disabled
                                />
                              </td>
                              <td>
                                <TextField
                                  value={"Infinity"}
                                  isDisabled
                                  className={css({
                                    maxWidth: "150px",
                                  })}
                                />
                              </td>
                              <td>
                                {testDesiredInventoryLevel && testInventoryLevel ?
                                  <span>
                                    If the pricing plan is over{" "}
                                    <span className="fw-bold" style={{ color: trellisPalette[14] }}>
                                      {(inventoryRulesField.value[3].min / 100) * testDesiredInventoryLevel}
                                    </span>{" "}
                                    it is considered to overstock. Pricing plans in this range will be very aggressive
                                    in lowering prices to improve the sell through rate.
                                  </span>
                                : <span>
                                    If the pricing plan is over{" "}
                                    <span className="fw-bold" style={{ color: trellisPalette[14] }}>
                                      {inventoryRulesField.value[3].min}%
                                    </span>{" "}
                                    of the desired days on hand level, it is overstock. Pricing plans in this range will
                                    be very aggressive in lowering prices to improve the sell through rate.
                                  </span>
                                }
                              </td>
                            </tr>
                          </>
                        )}
                      </ArrayField>
                    </tbody>
                  </Table>

                  <h5 className={css({ margin: 0, fontWeight: 700 })}>Test Your Rules</h5>

                  <ExplainerBox>
                    <div>
                      Test your rules by inputting an example of <em>desired days on hand</em> and the{" "}
                      <em>current days on hand</em> for a pricing plan. The system will then determine the appropriate
                      status for your pricing plan.
                    </div>
                  </ExplainerBox>
                  <HStack>
                    <NumberInputBase
                      label={"Desired Days on Hand"}
                      value={testDesiredInventoryLevel}
                      onChange={setTestDesiredInventoryLevel}
                    />
                    <NumberInputBase
                      label={"Example Days on Hand"}
                      value={testInventoryLevel}
                      onChange={setTestInventoryLevel}
                    />
                    <TextField
                      label={"Status"}
                      value={toTitleCase(
                        getInventoryRuleStatus(
                          testInventoryLevel,
                          testDesiredInventoryLevel,
                          form.values.inventory_rules ?? [],
                        ),
                      )}
                      isDisabled
                    />
                    <TextField
                      label={"Pricing Strategy"}
                      value={
                        ruleToStrategyMap[
                          getInventoryRuleStatus(
                            testInventoryLevel,
                            testDesiredInventoryLevel,
                            form.values.inventory_rules ?? [],
                          ) ?? "default"
                        ]
                      }
                      isDisabled
                    />
                  </HStack>
                </VStack>
              </Accordion>
            : null}
            <Center marginTop={"small"}>
              <Button
                isPending={form.submitting}
                onPress={async () => {
                  await form.submit(onSubmit);
                  form.setInitialValues(form.values);
                  await form.reset();
                }}
              >
                Save
              </Button>
            </Center>
          </Card>
        )}
      </FormConsumer>
    </FormProvider>
  );
}

export default Settings;
